import { PaymentMethodTypeInfo } from '../main/model';

export class AppConstants {
    public static readonly applicationName = 'rcp';
    public static readonly applicationTitle = 'Resurgent Customer Portal';
    public static readonly copyrightYear = 2004;
    public static readonly msgErrAccountNumber = 'Account number is required';
    public static readonly msgErrLast4Ssn = 'Last four of SSN is required';
    public static readonly msgErrEmailAddress = 'Email address is required';
    public static readonly paymentOptions = {
        pif: 1,
        sif: 2,
        other: 3,
        sifPlan: 4,
        otherPlan: 5,
        pifPlan: 6
    };
    public static readonly paymentOptionNames = {
        1: 'Pif',
        2: 'Sif',
        3: 'Other',
        4: 'SifPlan',
        5: 'OtherPlan',
        6: 'PifPlan'
    };
    public static readonly planFrequencies = {
        monthly: 'Monthly',
        semiMonthly: 'Semi-Monthly',
        biWeekly: 'Bi-Weekly',
        weekly: 'Weekly',
        once: 'Once'
    };
    public static readonly paymentStatuses = {
        'Arranged': 1,
        'Authorized': 2,
        'Scheduled': 3,
        'Pending': 4,
        'Processing': 5,
        'Accepted': 6,
        'Rejected': 7,
        'Canceled': 8,
        'Returned': 9,
        'Error': 10,
        'Terminated': 11,
        'Moved': 12
    };
    public static readonly baseFrequencies = {
        'Monthly': 'Monthly',
        'Semi-Monthly': 'Monthly',
        'Bi-Weekly': 'Weekly',
        'Weekly': 'Weekly'
    };
    public static readonly planFrequencyLabels = {
        'Monthly': 'Monthly',
        'Semi-Monthly': 'Twice A Month',
        'Bi-Weekly': 'Every Two Weeks',
        'Weekly': 'Weekly'
    };
    public static monthsToFrequenyInstallments = {
        'Monthly': 1,
        'Semi-Monthly': 2,
        'Bi-Weekly': 26 / 12,
        'Weekly': 52 / 12
    };
    public static readonly regex = {
        currency: /^\d*?(\.[0-9][0-9]?)?$/,
        lastFour: /^\d{4}$/,
        verificationCode: /^\d{6}$/,
        nin: /(^\d{3}-\d{2}-\d{4}$)|(^\d{9}$)|(^\d{4}$)/,
        phone: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/, // 10 digit phone e.g. xxx-xxx-xxxx, xxxxxxxxx, (xxx)xxx-xxxx, xxx.xxx.xxxx
    };
    public static readonly accountStatuses = {
        unpaid: 'unpaid', // has a balance, can make a payment
        error: 'error', // error with one or more payments
        brokenPlan: 'brokenPlan', // active plan has a failed/rejected/returned pmt on an active plan
        paying: 'paying', // has a balance, has one or more payments scheduled
        finalPaymentPending: 'finalPaymentPending', // final payment of a payment plan is being processed
        resolved: 'resolved', // paid in full (PIF) or settled in full (SIF)
        unavailable: 'unavailable', // not available to view
        prelegal: 'prelegal' // on the prelegal track
    };
    public static readonly accountDisplayStatuses = {
        unpaid: 'No Payments Scheduled',
        error: 'Attention Required',
        brokenPlan: 'Attention Required',
        paying: '',
        finalPaymentPending: 'Final Payment Pending',
        resolved: 'Resolved',
        unavailable: '',
        prelegal: 'Prelegal Track'
    };
    public static readonly accountDisplayStatusIcons = {
        unpaid: 'no-payments-status.svg',
        error: 'attention-required-status.svg',
        brokenPlan: 'attention-required-status.svg',
        finalPaymentPending: 'final-payment-status.svg',
        resolved: 'resolved-status.svg',
        prelegal: 'attention-required-status.svg'
    };
    public static readonly accountStatusClasses = {
        '': '',
        unpaid: 'bg-warning-light',
        error: 'bg-danger-light',
        brokenPlan: 'bg-danger-light',
        paying: '',
        finalPaymentPending: 'bg-green-tea',
        resolved: 'bg-dusk',
        unavailable: '',
        prelegal: 'bg-danger-light'
    };
    public static readonly chevronDirections = {
        up: 'up',
        right: 'right',
        left: 'left',
        down: 'down'
    };
    public static readonly paymentToolTips = [
        'Payment Info',
        'Terms and Conditions',
        'Review',
        'Confirmation'
    ];
    public static readonly diyPaymentToolTips = [
        'Select Number of Payments', ...AppConstants.paymentToolTips
    ];
    public static readonly otherPaymentToolTips = [
        'Enter Payment Amount', ...AppConstants.paymentToolTips
    ];
    public static readonly accountLinkingToolTips = [
        'Find Your Account',
        'Verify Your Identity',
        'Terms and Conditions'
    ];
    public static readonly guestPaymentToolTips = [
        'Select Your Payment Options',
        'Payment Terms and Conditions',
        'Review Payment',
        'Payment Confirmation'
    ];
    public static readonly changeDateToolTips = [
        'Change Payment Date',
        'Review Payment Date Change',
        'Payment Date Change Confirmation'
    ];
    public static readonly defaultErrorMessage = {
        payment: 'There was an error scheduling your payment. Please wait a few minutes, refresh this page and try again.',
        verifyEmail: 'The email address you entered is invalid. Please fix it and try again.'
    };
    public static readonly contactFormStaticAccountOptions = {
        accountNotListed: 'Account Not Listed',
        notRelatedToAnAccount: 'Not Related to an Account',
    };
    public static readonly brwrEmailTypeNames = {
        1: 'Personal',
        2: 'Work',
        3: 'Personal',
        4: 'Personal',
        5: 'Personal',
        6: 'Personal',
        7: 'Work',
        8: 'Work',
        9: 'Work',
        10: 'Work',
        15: 'Combo'
    };
    public static readonly brwrEmailTypeIds = {
        'Personal': 1,
        'Work': 2,
        'Combo': 15
    };
    public static readonly brwrPhoneTypeNames = {
        1: 'Home',
        2: 'Work',
        3: 'Other',
        4: 'Mobile',
        5: 'Fax',
        10: 'Other'
    };
    public static readonly expirementClasses = {
        planTermsAsc: 'exp-plan-terms-asc'
    };
    public static readonly paymentMethodTypes = {
        ach: <PaymentMethodTypeInfo> {
            typeId: 1,
            type: 'ach',
            label: 'Checking / Savings Account',
            network: 'ACH',
            startEventAction: 'Add Checking Begin',
            completeEventAction: 'Funding Source Added Checking',
            primaryImageUrl: 'assets/checking.gif',
            defaultHeight: '434px',
            loaded: false
        },
        card: <PaymentMethodTypeInfo> {
            typeId: 2,
            type: 'card',
            label: 'Debit Card',
            network: 'CARD',
            startEventAction: 'Add Debit Begin',
            completeEventAction: 'Funding Source Added Debit',
            primaryImageUrl: 'assets/images/debit-card.svg',
            defaultHeight: '371px',
            loaded: false
        },
        payPal: <PaymentMethodTypeInfo> {
            typeId: 5,
            type: 'payPal',
            label: 'PayPal',
            network: 'PAYPAL',
            startEventAction: 'Add PayPal Begin',
            completeEventAction: 'Funding Source Added PayPal',
            primaryImageUrl: 'assets/images/paypal-logo.svg',
            secondaryImageUrl: 'assets/images/paypal-logo-white.svg',
            defaultHeight: '300px',
            hideLabel: true,
            loaded: false
        },
        venmo: <PaymentMethodTypeInfo> {
            typeId: 6,
            type: 'venmo',
            label: 'Venmo',
            network: 'VENMO',
            startEventAction: 'Add Venmo Begin',
            completeEventAction: 'Funding Source Added Venmo',
            primaryImageUrl: 'assets/images/venmo-logo-button.svg',
            secondaryImageUrl: 'assets/images/venmo-logo-white-button.svg',
            defaultHeight: '290px',
            hideLabel: true,
            loaded: false
        }
    };

    public static readonly contactSourceSystems = {
        rcpPreAuthContactUs: 'RCPPreAuthContactUs',
        rcpPostAuthContactUs: 'RCPPostAuthContactUs',
        rcpPreAuthContactCco: 'RCPPreAuthContactCco',
        rcpPostAuthContactCco: 'RCPPostAuthContactCco',
    };

    public static readonly cookieNames = {
        bing: 'rcp-bing-sourced-request',
        mfaSession: 'rcp-post-auth-token'
    };

    public static readonly mfaVerificationErrorMessages = {
        invalidVerificationCode: 'Incorrect Verification Code.  Please Try Again.',
        expiredVerificationCode: 'Verification Code Expired.  Re-send Verification Code to Retry.',
        tooManyAttempts: 'Too Many Failed Verification Code Attempts.  Re-send Verification Code to Retry.',
        missingVerificationCode: 'Verification Code Missing.  Re-send Verification Code to Retry.'
    };
}
